import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, Binoculars } from "lucide-react";

/**
 * MyCorner — Roc's dedicated personal space. Opt-in (lives in bottom dock,
 * not the top nav) so visitors choose to learn about the founder rather than
 * having it pushed at them. Each section is collapsible.
 */

const SECTIONS = [
  {
    key: "story",
    title: "My Story",
    teaser: "Bodybuilder · life coach · advocate for body-limited & LGBTQ+ athletes.",
    body: (
      <>
        <p>
          I'm <span className="text-[#D4AF37] font-bold">Roc May</span> — bodybuilder, life coach, and
          founder of OmniMuscle. Based in Pinole, California. I created this platform to build the
          inclusive space I always wished existed: a place where neurodivergent and disabled lifters
          could pursue strength and well-being on their own terms.
        </p>
        <p className="mt-3">
          I run the <span className="text-purple-300 font-bold">"Gay Muscle"</span> social media
          presence and online community — built to support body positivity, mental health
          conversations, and connection among marginalized fitness enthusiasts.
        </p>
        <p className="mt-3">
          I'm open about my own experience with <span className="text-purple-300 font-bold">autism</span>{" "}
          and <span className="text-purple-300 font-bold">Cerebral Palsy</span>. My coaching is built
          around helping people facing physical, mental, or developmental challenges build a healthy,
          stable relationship with their bodies.
        </p>
        {/* Blackstone Legion badge */}
        <a
          data-testid="blackstone-legion-badge"
          href="https://www.blackstonelabs.com"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-5 inline-flex items-center gap-3 border-2 border-red-700 bg-gradient-to-r from-[#1a0a0a] via-[#2a0808] to-[#1a0a0a] px-4 py-2.5 hover:border-red-500 hover:scale-[1.01] transition-all group"
        >
          <span className="text-2xl">💀</span>
          <div className="text-left">
            <div className="text-[10px] uppercase tracking-[0.3em] text-red-400 font-bold">
              Blackstone Legion Member
            </div>
            <div className="text-xs text-neutral-300 group-hover:text-white">
              Proud ambassador · blackstonelabs.com →
            </div>
          </div>
        </a>
      </>
    ),
  },
  {
    key: "training",
    title: "How I Train",
    teaser: "Machine-dominant · low-impact · 3×10 strength + 3×12–15 volume · mandatory mid-week rest.",
    body: (
      <>
        <p>
          As a bodybuilder navigating Cerebral Palsy and Autism, I adapt traditional bodybuilding
          into a <span className="text-[#D4AF37]">highly customized, low-impact</span> structure.
          Machine-based movements, targeted rep ranges, extended recovery — not your standard
          Push/Pull/Legs split.
        </p>
        <div className="mt-4 overflow-x-auto">
          <table className="text-sm text-neutral-300 border-collapse w-full">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[#D4AF37]">
                <th className="text-left py-2 pr-3 border-b border-zinc-800">Day</th>
                <th className="text-left py-2 pr-3 border-b border-zinc-800">Focus</th>
                <th className="text-left py-2 border-b border-zinc-800">Scheme</th>
              </tr>
            </thead>
            <tbody>
              <tr><td className="py-1.5 pr-3 border-b border-zinc-900 font-bold">Mon</td><td className="pr-3 border-b border-zinc-900">Upper Strength</td><td className="border-b border-zinc-900">3×10 to failure on the final 2 sets</td></tr>
              <tr><td className="py-1.5 pr-3 border-b border-zinc-900 font-bold">Tue</td><td className="pr-3 border-b border-zinc-900">Lower Strength</td><td className="border-b border-zinc-900">3×10 controlled tempo, protect joints</td></tr>
              <tr><td className="py-1.5 pr-3 border-b border-zinc-900 font-bold text-purple-300">Wed</td><td className="pr-3 border-b border-zinc-900 text-purple-300">Mandatory Rest</td><td className="border-b border-zinc-900">Mental decompression · non-negotiable</td></tr>
              <tr><td className="py-1.5 pr-3 border-b border-zinc-900 font-bold">Thu</td><td className="pr-3 border-b border-zinc-900">Upper Volume</td><td className="border-b border-zinc-900">3×12–15 · bump load when 15 feels easy</td></tr>
              <tr><td className="py-1.5 pr-3 border-b border-zinc-900 font-bold">Fri</td><td className="pr-3 border-b border-zinc-900">Lower Volume</td><td className="border-b border-zinc-900">3×12–15 same progression rule</td></tr>
              <tr><td className="py-1.5 pr-3 font-bold">Sat/Sun</td><td className="pr-3">Active Rest</td><td>Stretching, mobility, time with my partner</td></tr>
            </tbody>
          </table>
        </div>
        <ul className="mt-4 space-y-2 list-disc list-inside text-sm">
          <li><span className="text-[#D4AF37] font-bold">Machine-dominant isolation</span> — Single-Arm cable work, pendulum squat, Smith machine over heavy barbells.</li>
          <li><span className="text-[#D4AF37] font-bold">Unpowered-treadmill warmup</span> — 2 min forward + 2 min backward before lower-body days to safely activate hams + quads.</li>
          <li><span className="text-[#D4AF37] font-bold">Strict rest</span> — 90 sec standard sets, full 2 min for heavy leg work. Neurological fatigue is real.</li>
        </ul>
      </>
    ),
  },
  {
    key: "nutrition",
    title: "How I Eat",
    teaser: "Simple. Consistent. Digestive-friendly proteins + strategic carbs.",
    body: (
      <>
        <p>
          My nutrition is built around <span className="text-[#D4AF37] font-bold">digestive comfort,
          neuro-motor recovery, and high-quality nutrient density</span> — the things that actually
          fight the neurological fatigue lifting brings on with CP.
        </p>
        <ul className="mt-3 space-y-2 list-disc list-inside text-sm">
          <li><span className="font-bold text-white">Simplicity &amp; consistency</span> — same clean whole foods in repeatable patterns. Kills choice paralysis (a real thing for neurodivergent folks).</li>
          <li><span className="font-bold text-white">Satiating macronutrients</span> — clean proteins + healthy fats for snacks. Eggs, chicken, real oils.</li>
          <li><span className="font-bold text-white">Strategic carbs</span> — saved for main heavy meals. Processed sugars get cut.</li>
        </ul>
        <p className="mt-4 text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold">My supplement stack</p>
        <ul className="mt-1 space-y-1 list-disc list-inside text-sm text-neutral-300">
          <li><span className="text-white">EAAs intra-workout</span> — protect muscle, speed recovery</li>
          <li><span className="text-white">Whey Isolate</span> (e.g. Blackstone Labs Isolation) — fast-absorbing, no digestive strain</li>
          <li><span className="text-white">High-stim pre</span> (e.g. Blackstone Labs Dust-X) — focus + cognitive drive</li>
          <li><span className="text-white">Sleep / deep-recovery</span> (e.g. Blackstone Labs Anesthetized) — prevents executive burnout</li>
        </ul>
      </>
    ),
  },
  {
    key: "coaching",
    title: "How I Coach",
    teaser: "Mindset · adaptability · safe spaces. Patience over perfection.",
    body: (
      <>
        <div className="space-y-5">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#DC2626] font-bold mb-1">🧠 Mindset & Radical Self-Acceptance</div>
            <p className="text-sm text-neutral-300">
              Build a stable, loving relationship with your body — fitness is not a punishment. Mental
              health is part of the work, not a side conversation. I strip away the intimidating,
              exclusionary parts of gym culture so lifting feels psychologically safe.
            </p>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold mb-1">♿ Body-Limited Adaptability</div>
            <p className="text-sm text-neutral-300">
              Custom form modifications around spasticity, motor control, and joint limits. Strength
              training as the primary vehicle for functional autonomy. Progress is measured against
              YOUR baseline — never neurotypical or able-bodied metrics.
            </p>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-purple-300 font-bold mb-1">🏳️‍🌈 Safe Spaces & Collective Growth</div>
            <p className="text-sm text-neutral-300">
              Community-driven through OmniMuscle Facebook Group + Gay Muscle networks. Structured
              "Skills &amp; Strategies" routines reduce the executive dysfunction and choice paralysis
              neurodivergent folks face when starting out.
            </p>
          </div>
        </div>
      </>
    ),
  },
  {
    key: "mindset",
    title: "What I Believe",
    teaser: "Patience · positive self-talk · stack small wins · take needed rest.",
    body: (
      <>
        <p className="text-sm text-neutral-300">
          I don't do <span className="line-through">grind-until-you-break</span> gym tropes.
          Here's the actual playbook:
        </p>
        <ul className="mt-4 space-y-3">
          <li>
            <div className="font-bold text-white">Have patience with your progress.</div>
            <div className="text-sm text-neutral-400">Lifestyle alignment — mind, body, soul — is a movement, not a rapid transformation.</div>
          </li>
          <li>
            <div className="font-bold text-white">Run internal pep talks.</div>
            <div className="text-sm text-neutral-400">State your goals to yourself out loud at the start of each week. Don't let yesterday's struggle stay in your head.</div>
          </li>
          <li>
            <div className="font-bold text-white">Non-negotiable habits beat motivation.</div>
            <div className="text-sm text-neutral-400">Waiting for motivation is a trap. Clear the noise, build a small set of daily actions, and stack wins until they're part of your identity.</div>
          </li>
          <li>
            <div className="font-bold text-white">Take the rest you need.</div>
            <div className="text-sm text-neutral-400">Slowing down is a requirement for long-term health, not weakness.</div>
          </li>
        </ul>
        <div className="mt-6 border-l-4 border-[#D4AF37] pl-4 italic text-neutral-300">
          "Choose to rise." — every single day.
        </div>
      </>
    ),
  },
];

function CollapsibleSection({ section }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid={`corner-section-${section.key}`}
      className="border border-zinc-800 bg-[#0F0F0F] hover:border-[#D4AF37]/40 transition-colors"
    >
      <button
        data-testid={`corner-toggle-${section.key}`}
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left px-5 py-4 flex items-center justify-between gap-4"
        aria-expanded={open}
      >
        <div>
          <div className="font-display text-2xl text-white leading-tight">{section.title}</div>
          <div className="text-xs text-neutral-400 mt-0.5">{section.teaser}</div>
        </div>
        {open ? <ChevronUp size={20} className="text-[#D4AF37] flex-shrink-0" /> : <ChevronDown size={20} className="text-neutral-500 flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 text-neutral-200 leading-relaxed border-t border-zinc-900">
          {section.body}
        </div>
      )}
    </div>
  );
}

export default function MyCorner() {
  return (
    <section
      data-testid="my-corner"
      className="py-20 md:py-28 bg-gradient-to-b from-[#0A0A0A] via-[#150a25] to-[#0A0A0A] min-h-screen"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-purple-700/50 text-purple-300 mb-5">
            <Binoculars size={14} />
            <span className="font-accent text-xs tracking-[0.3em] uppercase">The Owner's Perch</span>
          </div>
          <h1 className="font-display text-4xl md:text-6xl text-white leading-[0.95] mb-4">
            The Person Behind{" "}
            <span style={{ background: "linear-gradient(90deg,#A855F7,#D4AF37)", backgroundClip: "text", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              OmniMuscle
            </span>
          </h1>
          <p className="text-neutral-400 max-w-2xl">
            Perched up high so you can see the whole flock. This is the optional, "read if you want to" view.
            Tap a card to expand it. OmniMuscle is about <span className="text-white font-bold">the movement</span> — but if
            you want to know who's holding the door open, here's me. 🐦‍🔥
          </p>
        </div>
        <div className="space-y-3">
          {SECTIONS.map((s) => (
            <CollapsibleSection key={s.key} section={s} />
          ))}
        </div>

        <div className="mt-12 text-center text-xs text-neutral-500">
          <Link to="/" className="hover:text-[#D4AF37]">← Back to the homepage</Link>
        </div>
      </div>
    </section>
  );
}
